// stores/kanban.js
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../services/api'

export const COLUMNS = [
  { key: 'ideas',  label: 'Ideas',   icon: 'mdi-lightbulb-outline',   color: '#7C3AED' },
  { key: 'bugs',   label: 'Bugs',    icon: 'mdi-bug-outline',          color: '#DC2626' },
  { key: 'todo',   label: 'To Do',   icon: 'mdi-format-list-checks',   color: '#2563EB' },
  { key: 'doing',  label: 'Doing',   icon: 'mdi-progress-wrench',      color: '#D97706' },
  { key: 'test',   label: 'Test',    icon: 'mdi-test-tube',            color: '#0891B2' },
  { key: 'done',   label: 'Done',    icon: 'mdi-check-circle-outline', color: '#16A34A' },
]

export const PRIORITIES = [
  { value: 'low',      label: 'Low',      color: '#6B7280' },
  { value: 'medium',   label: 'Medium',   color: '#2563EB' },
  { value: 'high',     label: 'High',     color: '#D97706' },
  { value: 'critical', label: 'Critical', color: '#DC2626' },
]

export const useKanbanStore = defineStore('kanban', () => {
  const columns = ref({
    ideas: [], bugs: [], todo: [], doing: [], test: [], done: []
  })
  const loading = ref(false)
  const teamMembers = ref([])
  const activeCard = ref(null)
  const comments = ref([])

  async function fetchBoard() {
    loading.value = true
    try {
      const { data } = await api.get('/kanban/cards')
      columns.value = data.columns
    } finally {
      loading.value = false
    }
  }

  async function fetchTeam() {
    try {
      const { data } = await api.get('/admin/users?role=admin,moderator&limit=50')
      teamMembers.value = data.users || []
    } catch {}
  }

  async function createCard(payload) {
    const { data } = await api.post('/kanban/cards', payload)
    await fetchBoard()
    return data.cardId
  }

  async function updateCard(cardId, payload) {
    await api.put(`/kanban/cards/${cardId}`, payload)
    await fetchBoard()
    // Sync activeCard with updated data from the refreshed board
    if (activeCard.value?.cardId === cardId) {
      for (const col of Object.values(columns.value)) {
        const updated = col.find(c => c.cardId === cardId)
        if (updated) { activeCard.value = updated; break }
      }
    }
  }

  async function moveCard(cardId, toColumn, toIndex) {
    // Optimistic update
    let card = null
    for (const col of Object.values(columns.value)) {
      const idx = col.findIndex(c => c.cardId === cardId)
      if (idx !== -1) { card = col.splice(idx, 1)[0]; break }
    }
    if (!card) return
    card.column = toColumn
    columns.value[toColumn].splice(toIndex, 0, card)

    // Persist
     await api.put(`/kanban/cards/${cardId}`, {
      column: toColumn,
      position: toIndex,
    })
  }

  async function deleteCard(cardId) {
    await api.delete(`/kanban/cards/${cardId}`)
    for (const col of Object.values(columns.value)) {
      const idx = col.findIndex(c => c.cardId === cardId)
      if (idx !== -1) { col.splice(idx, 1); break }
    }
  }

  async function fetchComments(cardId) {
    try {
      const { data } = await api.get(`/kanban/cards/${cardId}/comments`)
      comments.value = data.comments
    } catch (e) {
      console.error('fetchComments failed:', e?.response?.data || e.message)
      comments.value = []
    }
  }

  async function addComment(cardId, body, parentId) {
    await api.post(`/kanban/cards/${cardId}/comments`, { body, parentId })
    await fetchComments(cardId)
  }

  function setActiveCard(card) {
    activeCard.value = card
    if (card) fetchComments(card.cardId)
    else comments.value = []
  }

  return {
    columns, loading, teamMembers, activeCard, comments,
    fetchBoard, fetchTeam, createCard, updateCard,
    moveCard, deleteCard, fetchComments, addComment, setActiveCard,
  }
})